import z from 'zod'
import { objectToSnake } from 'ts-case-convert'
import { API, handleError, handleFormError } from './api'
import {
  collectionAdminExtendedSchema,
  collectionBrandSchema,
  collectionExtendedSchema,
  collectionFiltersSchema,
  collectionMetricsSchema,
  collectionSchema,
  createCollectionDataSchema,
  fabricHealthMetricsSchema,
  updateCollectionDataSchema,
} from './schemas'
import { cleanObject } from '../helpers'

export async function getCollection(id: number | 'favorites', rawParams: Record<string, unknown> = {}) {
  try {
    const params = getCollectionParams.parse(rawParams)
    const response = await API.get(`collections/${id}`, { params })
    const collection = collectionExtendedSchema.parse(response.data)

    return collection
  } catch (error) {
    return handleError(error, 'Collection')
  }
}

export async function getAdminCollection(id: number | 'favorites') {
  try {
    const response = await API.get(`collections/${id}`)
    const collection = collectionAdminExtendedSchema.parse(response.data)

    return collection
  } catch (error) {
    return handleError(error, 'Collection')
  }
}

export async function getCollections(rawParams: Record<string, unknown>) {
  try {
    const parsedParams = getCollectionsParams.parse(rawParams)
    const params = cleanObject(parsedParams)
    const response = await API.get('collections', { params })
    const collections = z.array(collectionSchema).parse(response.data)

    return { collections, totalCollections: response.headers.total }
  } catch (error) {
    return handleError(error, 'Collections')
  }
}

export async function getCollectionFilters(rawParams: Record<string, unknown>) {
  try {
    const params = getCollectionFiltersParams.parse(rawParams)

    const response = await API.get('collections/filters', { params })
    const filters = collectionFiltersSchema.parse(response.data)

    return filters
  } catch (error) {
    return handleError(error, 'Collection Filters')
  }
}

export async function getCollectionMetrics(rawParams: Record<string, unknown>) {
  try {
    const params = getCollectionsMetricsParams.parse(rawParams)
    const response = await API.get(`collections/health_metrics`, { params })
    const metrics = z.array(collectionMetricsSchema).parse(response.data)

    return { metrics, totalCollections: response.headers.total }
  } catch (error) {
    return handleError(error, 'Collection Metrics')
  }
}

export async function getCollectionBrands() {
  try {
    const response = await API.get('collection_brands')
    const brands = z.array(collectionBrandSchema).parse(response.data)

    return brands
  } catch (error) {
    return handleError(error, 'Collection Brands')
  }
}

export async function getCollectionFabricHealthMetrics(collectionId: number) {
  try {
    const response = await API.get(`collections/${collectionId}/fabric_metrics`)
    const metrics = fabricHealthMetricsSchema.parse(response.data)

    return metrics
  } catch (error) {
    return handleError(error, 'Collection Fabric Health Metrics')
  }
}

export async function createCollection(rawParams: Record<string, unknown>) {
  try {
    const params = createCollectionDataSchema.parse(rawParams)
    const response = await API.post(`collections`, params)

    const collection = collectionAdminExtendedSchema.parse(response.data)
    return { collection }
  } catch (error) {
    return handleFormError(error, 'Create Collection')
  }
}

export async function updateCollection(collectionId: number, rawParams: Record<string, unknown>) {
  try {
    const params = updateCollectionDataSchema.parse(rawParams)
    await API.put(`collections/${collectionId}`, params)

    return { error: false, message: 'Collection Updated', fields: undefined }
  } catch (error) {
    return handleFormError(error, 'Update Collection')
  }
}

export async function activateCollection(collectionId: number, rawParams: Record<string, unknown>) {
  try {
    const params = z
      .object({ active: z.preprocess(val => (val === 'false' ? false : Boolean(val)), z.boolean()) })
      .parse(rawParams)
    await API.put(`collections/${collectionId}/update_fabrics`, params)

    return { error: false, message: `Collection ${params.active ? 'Activated' : 'Deactivated'}` }
  } catch (error) {
    return handleFormError(error, 'Activate Collection')
  }
}

export async function generateCollection(collectionId: number) {
  try {
    await API.put(`collections/${collectionId}/generate_picario_data`)

    return { error: false, message: 'Picario Image Generation Job started' }
  } catch (error) {
    return handleFormError(error, 'Generate Collection')
  }
}

//* ---------------- PARAMS ---------------- *//
const getCollectionParams = z
  .object({
    showArchived: z.union([z.literal('1'), z.literal('0')]).optional(),
  })
  .transform(objectToSnake)
const getCollectionsParams = z
  .object({
    q: z.string().optional(),
    page: z.coerce.number().default(1),
    perPage: z.coerce.number().default(23),
    alphaSort: z.string().optional(),
    featured: z.string().optional(),
    reverseSort: z.string().default('true'),
    garmentType: z.string().optional(),
    trimGarmentType: z.string().optional(),
    isLive: z.string().default('true'),
    isDemo: z.string().optional(),
    crossCoded: z.enum(['true', 'false']).optional(),
    usage: z.array(z.enum(['shirting', 'clothing', 'shirting-trim', 'clothing-trim'])).optional(),
    year: z.array(z.string()).optional(),
    brand: z.array(z.string()).optional(),
    visualize: z.boolean().optional(),
  })
  .transform(({ alphaSort, featured, reverseSort, isLive, isDemo, ...data }) => ({
    ...data,
    alphaSort: alphaSort === 'true' ? 1 : undefined,
    featured: featured === 'true' ? 1 : undefined,
    reverseSort: reverseSort === 'true' ? 1 : undefined,
    isLive: isLive === 'true' ? 1 : isLive == 'false' ? 0 : undefined,
    isDemo: isDemo === 'true' ? 1 : undefined,
  }))
  .transform(objectToSnake)

const getCollectionFiltersParams = z
  .object({
    isLive: z.string().default('true'),
    crossCoded: z.enum(['true', 'false']).optional(),
    usage: z.array(z.enum(['shirting', 'clothing', 'shirting-trim', 'clothing-trim'])).optional(),
    trimGarmentType: z.string().optional(),
    year: z.array(z.string()).optional(),
    visualize: z.boolean().optional(),
  })
  .transform(objectToSnake)

const getCollectionsMetricsParams = z
  .object({
    collectionType: z.enum(['shirting', 'clothing', 'trim']).optional(),
    collectionIds: z.array(z.coerce.number()).optional(),
    isLive: z.enum(['true', 'false']).optional(),
    page: z.coerce.number().default(1),
    perPage: z.coerce.number().default(25),
  })
  .transform(objectToSnake)
